import moment from 'moment'
import 'moment-timezone'

/**
 * Utility functions for validating and filtering "Needed by" dates in demand requests
 */

// The Columbia Distribution Center (and its cutoff rules below) operates on
// Central Time regardless of the browser's local time zone, so "today" and
// the cutoff-hour checks must be anchored there rather than to local time.
const CENTRAL_TIME_ZONE = 'America/Chicago'
// Stock Request Form: Next-Day Air must be requested before 2pm CT.
const STOCK_REQUEST_CUTOFF_HOUR = 14
// Fulfillment Order creation: the order must be created before 3pm CT to
// still make the day's last wave.
const FULFILLMENT_ORDER_CUTOFF_HOUR = 15

const getCentralNow = (): moment.Moment => moment().tz( CENTRAL_TIME_ZONE )

// The rest of this module works in plain, zone-less moments (calendar dates
// compared/added without regard to time zone) so it composes safely with
// `moment(dateString)` elsewhere in the app. Rather than thread a
// Central-Time-zoned moment through that logic — which risks off-by-one
// mismatches when compared against zone-less moments — this converts "today
// in Central Time" into a plain moment representing that same calendar date.
const getCentralToday = (): moment.Moment => moment( getCentralNow().format( 'YYYY-MM-DD' ) )

/**
 * Filters out weekends (Saturday and Sunday)
 * @param date - The date to check
 * @returns true if the date is a weekday, false if it's a weekend
 */
export const filterWeekends = ( date: moment.Moment ): boolean => {
	const dayOfWeek = date.day()
	return dayOfWeek !== 0 && dayOfWeek !== 6
}

/**
 * Advances `start` forward by `days` business days (skipping weekends).
 */
const addBusinessDays = ( start: moment.Moment, days: number ): moment.Moment => {
	const date = start.clone()
	let added = 0
	while ( added < days ) {
		date.add( 1, 'day' )
		if ( filterWeekends( date ) ) {
			added++
		}
	}
	return date
}

/**
 * Given a cutoff hour (in Central Time), returns the earliest allowed "Needs
 * by" date: the next business day, or the next business day after that if
 * it's already past the cutoff hour today.
 */
const getCutoffMinDate = ( cutoffHour: number ): moment.Moment => {
	const businessDaysToAdd = getCentralNow().hour() >= cutoffHour ? 2 : 1
	return addBusinessDays( getCentralToday(), businessDaysToAdd )
}

/**
 * Returns the minimum allowed "Needs by" date for the Stock Request Form:
 * the next business day, or the next business day after that if it's past
 * the 2pm CT Next-Day Air cutoff.
 */
export const getMinNeededByDate = (): moment.Moment => getCutoffMinDate( STOCK_REQUEST_CUTOFF_HOUR )

/**
 * Returns the minimum allowed "Needs by" date for a Fulfillment Order: the
 * next business day, or the next business day after that if it's past the
 * 3pm CT cutoff for creating the order.
 */
export const getMinFulfillmentOrderNeededByDate = (): moment.Moment => getCutoffMinDate( FULFILLMENT_ORDER_CUTOFF_HOUR )

/**
 * Returns the next business day (weekday) after today. If tomorrow falls on a
 * weekend, this skips ahead to the following Monday.
 */
export const getNextBusinessDay = (): moment.Moment => {
	return addBusinessDays( getCentralToday(), 1 )
}

/**
 * Given a Stock Request's "Needs by" date, returns the date that should
 * populate a Fulfillment Order created from it: the original date, floored to
 * the minimum date currently allowed on the Fulfillment Order (per the 3pm CT
 * cutoff) — so the default always lands on a date the FO's own date picker
 * actually allows to be selected.
 */
export const getFulfillmentOrderNeededByDate = ( stockRequestNeededBy: string | undefined | null ): string | undefined => {
	if ( !stockRequestNeededBy ) {
		return stockRequestNeededBy ?? undefined
	}

	const neededBy = moment( stockRequestNeededBy )
	const minDate = getMinFulfillmentOrderNeededByDate()
	return moment.max( neededBy, minDate ).format( 'YYYY-MM-DD' )
}

/**
 * Validates that a date string meets the requirements for a "Needed by" date:
 * - Must be at least one day in the future (not today or past)
 * - Cannot be on a weekend (Saturday or Sunday)
 *
 * @param dateString - The date string to validate (YYYY-MM-DD format)
 * @returns Object with isValid flag and optional error message
 */
export const validateNeededByDate = ( dateString: string ): { isValid: boolean, error?: string } => {
	if ( !dateString ) {
		// Empty date is valid (date is optional)
		return { isValid: true }
	}

	const neededByDate = moment( dateString )
	const minDate = getMinNeededByDate().startOf( 'day' )
	const errormessage = 'Must be future date and not on the weekend.'

	// Check if date is before the minimum allowed date
	if ( neededByDate.isBefore( minDate ) ) {
		return { isValid: false, error: errormessage }
	}

	// Check if date is a weekend using the shared filter function
	if ( !filterWeekends( neededByDate ) ) {
		return { isValid: false, error: errormessage }
	}

	return { isValid: true }
}

/**
 * Returns true if `dateString` is on or before 2nd-day: one business day after
 * `getMinNeededByDate()`, so it follows the same 2pm CT cutoff. The DC treats
 * these as next-day / 2nd-day transit (expedite) requests. Mirrors the
 * `needed_by <= get_expedite_cutoff_date()` check in inventory-api's
 * CreateDemandRequest.
 */
export const isExpedite = ( dateString?: string ): boolean => {
	if ( !dateString ) {
		return false
	}

	const date = moment( dateString ).startOf( 'day' )
	const cutoff = addBusinessDays( getMinNeededByDate(), 1 )
	return date.isSameOrBefore( cutoff, 'day' )
}

/**
 * Calculates the number of business days (weekdays) between two dates
 * @param startDate - The starting date
 * @param endDate - The ending date
 * @returns The number of business days between the dates
 */
export const calculateBusinessDays = ( startDate: moment.Moment, endDate: moment.Moment ): number => {
	let businessDays = 0
	const current = moment( startDate ).startOf( 'day' )
	const end = moment( endDate ).startOf( 'day' )

	while ( current.isBefore( end ) ) {
		if ( filterWeekends( current ) ) {
			businessDays++
		}
		current.add( 1, 'day' )
	}

	return businessDays
}
